import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { d as debounce_1 } from './index4.js';
import { F as FilterService, s as sortBy } from './index2.js';
import { b as breakpoints } from './index5.js';
import { D as DomService } from './index3.js';
import { t as therapeuticAreasValues, T as TherapeuticAreas, d as defineCustomElement$2 } from './roche-pipeline-changes-row2.js';
import { d as defineCustomElement$4 } from './roche-headline2.js';
import { d as defineCustomElement$3 } from './roche-pipeline-caption2.js';

const pipelineChangesData = [];
const FilterPipelineChanges = {
    sortElements(content, sortParam) {
        return sortBy(content, sortParam);
    },
    groupElements(content, groupParam) {
        return FilterService.groupElements(content, groupParam);
    },
    handleTypes(content) {
        const typesKeys = Object.keys(content);
        typesKeys.forEach((key) => {
            if (key === 'draft') {
                return;
            }
            this.handleChanges(content, key);
        });
        return pipelineChangesData;
    },
    handleChanges(content, typesKey) {
        const changesValues = Object.values(content[typesKey]);
        content[typesKey].forEach((changes, index) => {
            const { name, genericName, projectStatus, phase, region, indicationShort, therapeuticArea, } = JSON.parse(JSON.stringify(changes));
            const temporaryObject = {
                name,
                genericName,
                projectStatus,
                phase,
                region,
                indicationShort,
                therapeuticArea,
                isBorderVisible: false,
                isStatusVisible: false,
                changes: null,
            };
            if (index === 0) {
                temporaryObject.isStatusVisible = true;
                temporaryObject.changes = changesValues.length;
                return pipelineChangesData.push(Object.assign({}, temporaryObject));
            }
            if (changesValues.length - 1 === index) {
                temporaryObject.isBorderVisible = true;
                return pipelineChangesData.push(Object.assign({}, temporaryObject));
            }
            return pipelineChangesData.push(Object.assign({}, temporaryObject));
        });
    },
};

const rochePipelineChangesTableCss = ":host{display:block;margin-top:3rem}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}:host .pipeline-table-headers{display:grid;grid-template-columns:repeat(5, 1fr);border-bottom:1px solid #eae8e5;padding-bottom:1.25rem;margin-bottom:2.5rem}@media (min-width: 64rem){:host .pipeline-table-headers{grid-template-columns:repeat(6, 1fr)}}:host .pipeline-table-headers .header{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--footnotes--font-size--small-up);line-height:1.1875rem;color:#544f4f;padding-left:0.4375rem}@media (min-width: 48rem){:host .pipeline-table-headers .header{font-size:var(--footnotes--font-size--medium-up)}}@media (min-width: 64rem){:host .pipeline-table-headers .header{padding-left:1.875rem}:host .pipeline-table-headers .header:nth-child(2){padding:0}}:host .pipeline-table-headers .header:first-of-type{display:none;padding:0}@media (min-width: 64rem){:host .pipeline-table-headers .header:first-of-type{display:block}}:host .pipeline-row-container{display:grid;grid-template-columns:1fr;grid-template-rows:auto;grid-gap:0}:host .pipeline-row-container roche-headline{margin-top:0.625rem}@media (min-width: 48rem){:host .pipeline-row-container roche-headline{margin-top:0.625rem}}@media (min-width: 64rem){:host .therapeutic-area-container{display:grid;grid-template-columns:16.6% 83.4%}}:host .therapeutic-area-list{grid-column:2;display:block;list-style-type:none;padding:0;margin:2.5rem 0 0 0}@media (min-width: 64rem){:host .therapeutic-area-list{display:flex}}:host .therapeutic-area-list__item{display:flex;flex-direction:row;margin-top:1rem}:host .therapeutic-area-list__item:first-of-type{margin-top:0}@media (min-width: 64rem){:host .therapeutic-area-list__item{flex-direction:column;margin:0 0 0 1.5rem}:host .therapeutic-area-list__item:first-of-type{margin-left:0}}:host .therapeutic-area-list__text{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--footnotes--font-size--small-up);line-height:1.1875rem;text-transform:capitalize}@media (min-width: 48rem){:host .therapeutic-area-list__text{font-size:var(--footnotes--font-size--medium-up)}}@media (min-width: 64rem){:host .therapeutic-area-list__text{margin-top:0.5rem}}:host .therapeutic-area-list__icon{display:inline-block;height:1.25rem;width:1.5rem;background-color:var(--therapeutic-area-color-light);position:relative;margin-right:0.5rem}@media (min-width: 64rem){:host .therapeutic-area-list__icon{margin:0}}:host .therapeutic-area-list__icon::before{content:\"\";position:absolute;top:0;left:0;height:100%;width:0.5rem;background-color:var(--therapeutic-area-color)}:host .therapeutic-area-list .oncology{--therapeutic-area-color:#ff7d29;--therapeutic-area-color-light:#fbe9dd}:host .therapeutic-area-list .immunology{--therapeutic-area-color:#e085fc;--therapeutic-area-color-light:#faeffe}:host .therapeutic-area-list .ophthalmology{--therapeutic-area-color:#1482fa;--therapeutic-area-color-light:#e7f2fe}:host .therapeutic-area-list .infectious-diseases{--therapeutic-area-color:#fac9b5;--therapeutic-area-color-light:#fbe9dd}:host .therapeutic-area-list .cardiovascular{--therapeutic-area-color:#26d07c;--therapeutic-area-color-light:#e8fbf1}:host .therapeutic-area-list .neuroscience{--therapeutic-area-color:#fbdd7a;--therapeutic-area-color-light:#fef9eb}:host .therapeutic-area-list .other{--therapeutic-area-color:#c2bab5;--therapeutic-area-color-light:#f1f0ef}:host roche-pipeline-caption{margin-top:2.5rem}:host([dir=rtl]) .pipeline-table-headers{direction:revert}:host([dir=rtl]) .header{padding-left:0;padding-right:0.4375rem}@media (min-width: 64rem){:host([dir=rtl]) .header{padding-left:0;padding-right:1.875rem}}:host([dir=rtl]) .therapeutic-area-list__icon{margin-right:0;margin-left:0.5rem}";
const RochePipelineChangesTableStyle0 = rochePipelineChangesTableCss;

const PipelineChangesCaption = [
    {
        abbreviation: 'CE - ',
        description: 'European Conformity;',
    },
    {
        abbreviation: 'US - ',
        description: 'FDA Approval;',
    },
    {
        abbreviation: 'WW - ',
        description: 'Worldwide including CE, US and China;',
    },
    {
        sup: '1 ',
        abbreviation: 'OUS - ',
        description: 'Outside the US; PCR:  Polymerase Chain Reaction; RT: Real Time;',
    },
    {
        sup: '2 ',
        abbreviation: 'EUA - ',
        description: 'Emergency Use Authorization;',
    },
    {
        sup: '3 ',
        description: 'Only selected countries',
    },
];
const RochePipelineChangesTable$1 = /*@__PURE__*/ proxyCustomElement(class RochePipelineChangesTable extends HTMLElement {
    handleResize() {
        this.isMobile = window.innerWidth <= breakpoints.large;
    }
    get contentParsed() {
        return typeof this.content === 'string' ? JSON.parse(this.content) : this.content;
    }
    groupData() {
        var _a;
        if (((_a = this.contentParsed) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            const groupedResult = FilterPipelineChanges.groupElements(this.contentParsed, 'projectStatus');
            this.diagnosticsData = FilterPipelineChanges.handleTypes(groupedResult);
        }
    }
    async componentWillLoad() {
        this.groupData();
    }
    componentDidLoad() {
        this.handleResize();
        DomService.handleTextDirection(this.element);
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.content = undefined;
        this.therapeuticArea = undefined;
        this.diagnosticsData = [];
        this.isMobile = false;
        this.handleResize = debounce_1(this.handleResize.bind(this), 50);
    }
    render() {
        const foundOncology = this.contentParsed ? this.contentParsed.some((item) => item.therapeuticArea === 'Oncology') : false;
        const foundOncologyHematology = this.contentParsed ? this.contentParsed.some((item) => item.therapeuticArea === 'Oncology/Hematology') : false;
        return (h(Host, { key: '86053d2f669b2caeadad8e61da757f6cadb21d6a' }, h("div", { key: '19df46d2f6eea8d5daa1eb309775cf20f4b6c7fa', class: "pipeline-table-headers" }, h("div", { key: 'f493e312d9958f855e92a897e630d4ca6ca03d5b', class: "header" }), h("div", { key: 'ed7a9d97dc8c7182b0bc9c5f3eaaf80109e83dca', class: "header" }, "Phase 1"), h("div", { key: '200977368a9c5e292f714bb8d2e0064f0bed4f7d', class: "header" }, "Phase 2"), h("div", { key: 'c5833eda4995215c33e9b78759a5db4ac5a498c7', class: "header" }, "Phase 3"), h("div", { key: 'b176e5c15ed21132acab86f6a18959591249e756', class: "header" }, "Filed"), h("div", { key: 'fd0dabbf475af35fbbcd4a1122078401ba09bf46', class: "header" }, "Approved")), h("div", { key: '645164710476457e8dcce0b5bc920c874b5358d4', class: "pipeline-row-container" }, this.diagnosticsData.map((item, index) => (h("roche-pipeline-changes-row", { key: index, name: item.name, "generic-name": item.genericName, phase: item.phase, region: item.region, "therapeutic-area": item.therapeuticArea, "indication-short": item.indicationShort, "project-status": item.projectStatus, "is-status-visible": item.isStatusVisible, changes: item.changes, "is-border-visible": item.isBorderVisible, "is-mobile": this.isMobile }))), h("div", { key: '812cd3ebf3b7beebe5b173a85c2e5321c8441796', class: "therapeutic-area-container" }, h("ul", { key: 'b673dfa5e5c40a4dcacb9516ecba6035e92fc095', class: "therapeutic-area-list" }, therapeuticAreasValues.map((item, index) => {
            const therapeuticArea = item.toLowerCase();
            if (therapeuticArea === TherapeuticAreas.ONCOLOGY
                && foundOncologyHematology) {
                return null;
            }
            if (therapeuticArea === TherapeuticAreas.ONCOLOGY_HEMATOLOGY
                && foundOncology) {
                return null;
            }
            return ((h("li", { key: index, class: "therapeutic-area-list__item" }, h("div", { class: {
                    'therapeutic-area-list__icon': true,
                    oncology: therapeuticArea === TherapeuticAreas.ONCOLOGY
                        || therapeuticArea === TherapeuticAreas.ONCOLOGY_HEMATOLOGY,
                    immunology: therapeuticArea === TherapeuticAreas.IMMUNOLOGY,
                    ophthalmology: therapeuticArea === TherapeuticAreas.OPHTHALMOLOGY,
                    'infectious-diseases': therapeuticArea === TherapeuticAreas.INFECTIOUS_DISEASES,
                    cardiovascular: therapeuticArea === TherapeuticAreas.CARDIOVASCULAR,
                    neuroscience: therapeuticArea === TherapeuticAreas.NEUROSCIENCE,
                    other: therapeuticArea === TherapeuticAreas.OTHER,
                } }), h("div", { class: "therapeutic-area-list__text" }, therapeuticArea))));
        })))), h("roche-pipeline-caption", { key: '4ad15229c25e44ea94878657fc83017dfd4dc194', "caption-string": (JSON.stringify(PipelineChangesCaption)) })));
    }
    get element() { return this; }
    static get style() { return RochePipelineChangesTableStyle0; }
}, [1, "roche-pipeline-changes-table", {
        "content": [1],
        "therapeuticArea": [1, "therapeutic-area"],
        "diagnosticsData": [32],
        "isMobile": [32]
    }, [[9, "resize", "handleResize"]]]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-pipeline-changes-table", "roche-headline", "roche-pipeline-caption", "roche-pipeline-changes-row"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-pipeline-changes-table":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RochePipelineChangesTable$1);
            }
            break;
        case "roche-headline":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "roche-pipeline-caption":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "roche-pipeline-changes-row":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const RochePipelineChangesTable = RochePipelineChangesTable$1;
const defineCustomElement = defineCustomElement$1;

export { RochePipelineChangesTable, defineCustomElement };

//# sourceMappingURL=roche-pipeline-changes-table.js.map